import React from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import type { MusicVideoLyricLine } from 'fym-shared/src/music24/musicVideoApi';
import { TypedMemo } from '../../common/types';
import { catalogSongPath } from '../../smartLinks/catalog/song/page/catalogSongPath';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { panelMessages } from '../messages/panelMessages';
import { formatTime } from '../timeline/timelineGeometry';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** A selected lyric line (read only here): its words, its time and "Edit the timing" (design d1). */
const LyricInspectorComponent: React.FC<{ line: MusicVideoLyricLine }> = ({ line }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { accountId, catalogItemUuid, mode } = useMusicVideoEditor();
  return (
    <>
      <h4 className={styles.inspectorHead}>
        {intl.formatMessage(panelMessages.lyricHeading, { number: line.index + 1 })}
        {line.startMs !== null && (
          <span className={styles.inspectorSub}>
            {intl.formatMessage(panelMessages.lyricTime, {
              from: formatTime(line.startMs / 1000, true),
              to: line.endMs === null ? '…' : formatTime(line.endMs / 1000, true),
            })}
          </span>
        )}
      </h4>
      <p className={styles.prompt}>{line.text}</p>
      {line.startMs === null && <p className={styles.hint}>{intl.formatMessage(panelMessages.lyricNoTime)}</p>}
      {/* The showcase song has no song page. */}
      {mode === 'account' && (
        <button
          type="button"
          className={styles.ghostButton}
          onClick={() => {
            // eslint-disable-next-line no-void -- a router navigation: nothing to wait for
            void navigate(catalogSongPath(catalogItemUuid, { section: 'lyrics', accountId }));
          }}
        >
          {intl.formatMessage(panelMessages.editTiming)}
        </button>
      )}
      <span className={styles.grow} />
      <p className={styles.hint}>{intl.formatMessage(panelMessages.lyricsHint)}</p>
    </>
  );
};

export const LyricInspector = TypedMemo(LyricInspectorComponent);
